import React, { useCallback, useEffect, useState } from 'react';
import { GoogleMap, useJsApiLoader, Polygon } from '@react-google-maps/api';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { TrafficAreaEnum } from '../../../models/tariff/trafficAreas/TrafficAreaEnum';
import { GOOGLE_MAPS_API_KEY, USE_LEAFLET_MAPS } from '../../../config';
import { Position } from 'geojson';
import MapJLSLeaflet from './MapJLSLeaflet';

const containerStyle = {
    width: '100%',
    height: '260px',
    border: '1px solid transparent',
    borderRadius: '15px'
};

const containerStyleFullView = {
    width: '100%',
    height: '520px'
};

const polygonStrokeColor = '#102A83';
const polygonFillColor = 'rgba(58, 114, 255, 1)';

interface Props {
    trafficAreas: number[];
    fullView?: boolean;
}

interface LatLng {
    latitude: number;
    longitude: number;
}

type Coordinates = LatLng[];

function MapJLS(props: Props) {
    if (USE_LEAFLET_MAPS)
        return <MapJLSLeaflet {...props} />;

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY
    });

    const [map, setMap] = React.useState<google.maps.Map | undefined>(undefined);
    const [jlsCoords, setJlsCoords] = useState<Coordinates[][] | undefined>(undefined);

    const trafficAreasGeography = useSelector((state: ApplicationState) => state.tariff.trafficAreasGeography);

    const getTrafficAreaCoordinates = useCallback((item: number): Coordinates => {
        const itemGeometry = trafficAreasGeography?.find((t) => t.externalId === item)?.geography;

        if (!itemGeometry)
            return [];

        let itemCoordinates: Position[] = [];

        if (itemGeometry.type === 'Polygon') {
            itemCoordinates = itemGeometry.coordinates.flat(1);
        } else if (itemGeometry.type === 'MultiPolygon') {
            itemCoordinates = itemGeometry.coordinates.flat(2);
        }

        return itemCoordinates.map((coordinate) => ({ latitude: coordinate[1], longitude: coordinate[0] }));
    }, [trafficAreasGeography]);

    const findTrafficArea = useCallback((item: number): Coordinates[] | undefined => {
        if (!trafficAreasGeography)
            return undefined;

        if (item === TrafficAreaEnum.All) {
            return trafficAreasGeography.map((trafficAreaGeography) => getTrafficAreaCoordinates(trafficAreaGeography.externalId));
        }

        return [getTrafficAreaCoordinates(item)];
    }, [getTrafficAreaCoordinates, trafficAreasGeography]);

    const prepareJLSPolygon = useCallback((jlsArr?: number[]) => {
        const flattenCoords: Coordinates[] = [];
        const allJlsCoords: Coordinates[][] = [];

        jlsArr?.forEach((jls) => {
            const currentJlsCoords = findTrafficArea(jls);

            if (!currentJlsCoords)
                return;

            flattenCoords.push(currentJlsCoords.reduce((acc, val) => acc.concat(val), []));
            allJlsCoords.push(currentJlsCoords);
        });

        setJlsCoords(flattenCoords.length > 0 && allJlsCoords.length > 0 ? allJlsCoords : undefined);
    }, [findTrafficArea]);

    const onLoad = useCallback((googleMap: google.maps.Map) => {
        setMap(googleMap);
    }, []);

    const onUnmount = useCallback(() => {
        setMap(undefined);
    }, []);

    useEffect(() => {
        prepareJLSPolygon(props.trafficAreas);
    }, [prepareJLSPolygon, props.trafficAreas]);

    useEffect(() => {
        if (!map || !jlsCoords)
            return;

        const coordinates = parseToGoogleMapsCoordinates(jlsCoords.flat().flat());
        const bounds = new google.maps.LatLngBounds();

        coordinates.forEach((coordinate) => {
            bounds.extend(coordinate);
        });

        map.fitBounds(bounds);
        map.panToBounds(bounds);
    }, [jlsCoords, map]);

    const parseToGoogleMapsCoordinates = (coordinates: Coordinates): google.maps.LatLngLiteral[] => {
        return coordinates.map((coordinate) => ({ lat: Number(coordinate.latitude), lng: Number(coordinate.longitude) }));
    };

    if (!isLoaded)
        return <></>;

    return (
        <GoogleMap
            mapContainerStyle={props.fullView ? containerStyleFullView : containerStyle}
            onLoad={onLoad}
            onUnmount={onUnmount}
        >
            {jlsCoords?.map((coords, groupIndex) =>
                coords.map((polygon, polygonIndex) => (
                    <Polygon
                        key={`${groupIndex}-${polygonIndex}`}
                        path={parseToGoogleMapsCoordinates(polygon)}
                        options={{
                            fillColor: polygonFillColor,
                            strokeColor: polygonStrokeColor,
                            strokeWeight: 2
                        }}
                    />
                ))
            )}
        </GoogleMap>
    );
}

export default React.memo(MapJLS);
